import { Component, computed, inject, input } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import type { SortSelection } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { PriceHelper } from '@valk-nx/helpers/lib/price/price.helper';
import { buildIconListRoom } from '@valk-nx/storyblok-helpers/src/lib/general/icon-list.helper';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import type {
	FilterableRoom,
	TransformedFilterableRoom,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import { sortByOption } from '../../filterable-list.helpers';

type SortableFilterableRoom = FilterableRoom & { name: string };

@Component({
	host: { class: 'contents' },
	selector: 'sb-filterable-list-vdc-rooms',
	templateUrl: './vdc-rooms.html',
	imports: [AlertComponent, PersuadeCardComponent, TranslatePipe],
})
export class VdcRooms {
	private readonly filterableListService = inject(FilterableListService);
	private readonly bookingtoolUrl = inject(BOOKINGTOOL);

	sortOption = input.required<SortSelection>();
	showPrices = input<boolean>(true);

	rooms = computed<SortableFilterableRoom[]>(
		() =>
			this.filterableListService.vdcRooms
				.value()
				?.rooms.map((room) => ({ ...room, name: room.title })) ?? [],
	);

	sortedRooms = computed<SortableFilterableRoom[]>(() =>
		sortByOption(this.rooms(), this.sortOption()),
	);

	displayRooms = computed<TransformedFilterableRoom[]>(() => {
		const showPrices = this.showPrices();
		const languageCode = this.filterableListService.language();

		return this.sortedRooms().map((room) => ({
			...room,
			iconList: buildIconListRoom(room),
			pricingToolTip: PriceHelper.getPricingTooltip(
				room.occupancyRestrictions,
			),
			price: showPrices && room.isBookable ? room.price : undefined,
			ctaUrl: room.isBookable
				? BookingRedirectHelper.getBookingToolUrl(
						this.bookingtoolUrl,
						room.hotelSlug,
						{ roomGuid: room.guid, languageCode },
					)
				: undefined,
		}));
	});
}
